import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Save, Trash2, LayoutList, LayoutGrid, Clock, Play } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer';
import TeamGate from '../components/TeamGate';
import { toast } from '../hooks/use-toast';

const VideoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [clips, setClips] = useState([]);
  const [notes, setNotes] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showClipForm, setShowClipForm] = useState(false);
  const [newClip, setNewClip] = useState({ title: '', start_time: 0, end_time: 0 });
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [clipData, setClipData] = useState({
    down: '',
    distance: '',
    field_position: '',
    hash: '',
    personnel: '',
    formation: '',
    play_type: '',
    result: '',
    opponent: ''
  });

  useEffect(() => {
    // TODO: Fetch video data from API
    // For now, video data comes from parent component or API integration needed
  }, [id]);

  const handleAddNote = (timestamp) => {
    setCurrentTime(timestamp || currentTime);
    setShowNoteForm(true);
  };

  const handleSaveNote = () => {
    if (!newNote.trim()) return;

    const note = {
      id: `note_${Date.now()}`,
      video_id: id,
      timestamp: currentTime,
      content: newNote,
      author: 'Coach Smith',
      created_at: new Date().toISOString()
    };

    setNotes([...notes, note]);
    setNewNote('');
    setShowNoteForm(false);
    toast({ title: t('videoDetail.noteAdded') });
  };

  const handleDeleteNote = (noteId) => {
    setNotes(notes.filter(n => n.id !== noteId));
    toast({ title: t('videoDetail.noteDeleted') });
  };

  const handleClipMark = (startTime, endTime) => {
    setNewClip({ ...newClip, start_time: startTime, end_time: endTime });
    setShowClipForm(true);
  };

  const handleSaveClip = () => {
    if (!newClip.title.trim()) {
      toast({ title: t('videoDetail.enterClipTitle'), variant: 'destructive' });
      return;
    }

    const clip = {
      id: `clip_${Date.now()}`,
      video_id: id,
      ...newClip,
      play_data: clipData
    };

    setClips([...clips, clip]);
    setNewClip({ title: '', start_time: 0, end_time: 0 });
    setClipData({
      down: '', distance: '', field_position: '', hash: '', personnel: '',
      formation: '', play_type: '', result: '', opponent: ''
    });
    setShowClipForm(false);
    toast({ title: t('videoDetail.clipSaved') });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!video) return null;

  return (
    <TeamGate title={t('videoDetail.title') || 'Video Detayı'}>
      <div className="px-4 lg:px-8 pb-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/videos')}
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('videoDetail.backToVideos')}
          </Button>
          {/* <h1 className="text-3xl font-bold text-white mb-2">{video.title}</h1> */}
          {video.game_info && (
            <p className="text-gray-400">
              {video.game_info.date} • vs {video.game_info.opponent} • {video.game_info.score}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video Player - 2 columns */}
          <div className="lg:col-span-2">
            <VideoPlayer
              videoUrl={mockVideoUrl}
              onTimeUpdate={setCurrentTime}
              onClipMark={handleClipMark}
              onAddNote={handleAddNote}
              annotations={notes}
              onDeleteAnnotation={handleDeleteNote}
            />

            {/* Clip Form */}
            {showClipForm && (
              <Card className="bg-[#1a1a1b] border-gray-800 mt-4">
                <CardHeader>
                  <CardTitle className="text-white">{t('videoDetail.saveClip')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Input
                      placeholder={t('videoDetail.clipTitle')}
                      value={newClip.title}
                      onChange={(e) => setNewClip({ ...newClip, title: e.target.value })}
                      className="bg-[#0a0a0b] border-gray-700 text-white"
                    />
                  </div>
                  <div className="text-sm text-gray-400">
                    {formatTime(newClip.start_time)} → {formatTime(newClip.end_time)}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Select onValueChange={(v) => setClipData({ ...clipData, down: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                        <SelectValue placeholder={t('training.down')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1st</SelectItem>
                        <SelectItem value="2">2nd</SelectItem>
                        <SelectItem value="3">3rd</SelectItem>
                        <SelectItem value="4">4th</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder={t('training.distance')}
                      value={clipData.distance}
                      onChange={(e) => setClipData({ ...clipData, distance: e.target.value })}
                      className="bg-[#0a0a0b] border-gray-700 text-white"
                    />
                    <Input
                      placeholder={t('training.fieldPosition')}
                      value={clipData.field_position}
                      onChange={(e) => setClipData({ ...clipData, field_position: e.target.value })}
                      className="bg-[#0a0a0b] border-gray-700 text-white"
                    />
                    <Select onValueChange={(v) => setClipData({ ...clipData, hash: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                        <SelectValue placeholder={t('training.hash')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L">Left</SelectItem>
                        <SelectItem value="M">Middle</SelectItem>
                        <SelectItem value="R">Right</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(v) => setClipData({ ...clipData, personnel: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                        <SelectValue placeholder={t('training.personnel')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="11">11 Personnel</SelectItem>
                        <SelectItem value="12">12 Personnel</SelectItem>
                        <SelectItem value="21">21 Personnel</SelectItem>
                        <SelectItem value="22">22 Personnel</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder={t('training.formation')}
                      value={clipData.formation}
                      onChange={(e) => setClipData({ ...clipData, formation: e.target.value })}
                      className="bg-[#0a0a0b] border-gray-700 text-white"
                    />
                    <Select onValueChange={(v) => setClipData({ ...clipData, play_type: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                        <SelectValue placeholder={t('training.playType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pass">Pass</SelectItem>
                        <SelectItem value="Run">Run</SelectItem>
                        <SelectItem value="Special">Special Teams</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(v) => setClipData({ ...clipData, result: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                        <SelectValue placeholder={t('training.result')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TD">Touchdown</SelectItem>
                        <SelectItem value="Gain">Gain</SelectItem>
                        <SelectItem value="Loss">Loss</SelectItem>
                        <SelectItem value="Turnover">Turnover</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder={t('training.opponent')}
                      value={clipData.opponent}
                      onChange={(e) => setClipData({ ...clipData, opponent: e.target.value })}
                      className="bg-[#0a0a0b] border-gray-700 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveClip} className="bg-blue-600 hover:bg-blue-700">
                      <Save className="w-4 h-4 mr-2" />
                      {t('videoDetail.saveClip')}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowClipForm(false)} className="text-white">
                      {t('videoDetail.cancel')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - Notes & Clips */}
          <div>
            <Tabs defaultValue="notes" className="w-full">
              <TabsList className="bg-[#1a1a1b] border-gray-800 w-full">
                <TabsTrigger value="notes" className="flex-1">{t('videoDetail.notes')}</TabsTrigger>
                <TabsTrigger value="clips" className="flex-1">{t('videoDetail.clips')}</TabsTrigger>
              </TabsList>

              <TabsContent value="notes" className="mt-4">
                <Card className="bg-[#1a1a1b] border-gray-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white">{t('videoDetail.notes')}</CardTitle>
                      <Button
                        size="sm"
                        onClick={() => handleAddNote(currentTime)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('videoDetail.add')}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                    {showNoteForm && (
                      <div className="space-y-2 p-3 bg-[#0a0a0b] rounded-lg">
                        <Textarea
                          placeholder="Enter your note..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="bg-[#1a1a1b] border-gray-700 text-white min-h-[80px]"
                        />
                        <div className="text-xs text-gray-400 mb-2">@ {formatTime(currentTime)}</div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveNote} className="bg-blue-600 hover:bg-blue-700">
                            <Save className="w-3 h-3 mr-1" />
                            {t('videoDetail.save')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowNoteForm(false)} className="text-white">
                            {t('videoDetail.cancel')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {notes.map((note) => (
                      <div key={note.id} className="p-3 bg-[#0a0a0b] rounded-lg hover:bg-gray-800/50 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs text-blue-400 font-mono">{formatTime(note.timestamp)}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-400">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="text-sm text-white mb-2">{note.content}</p>
                        <p className="text-xs text-gray-500">{note.author} • {new Date(note.created_at).toLocaleString()}</p>
                      </div>
                    ))}

                    {notes.length === 0 && !showNoteForm && (
                      <p className="text-center text-gray-500 py-8">{t('videoDetail.noNotes')}</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="clips" className="mt-4">
                <Card className="bg-[#1a1a1b] border-gray-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white">{t('videoDetail.clips')} ({clips.length})</CardTitle>
                      <div className="flex bg-[#0a0a0b] p-1 rounded-md border border-gray-800">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewMode('list')}
                          className={`h-7 w-7 p-0 ${viewMode === 'list' ? 'bg-gray-800 text-blue-400' : 'text-gray-500'}`}
                        >
                          <LayoutList className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewMode('grid')}
                          className={`h-7 w-7 p-0 ${viewMode === 'grid' ? 'bg-gray-800 text-blue-400' : 'text-gray-500'}`}
                        >
                          <LayoutGrid className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                    {viewMode === 'grid' ? (
                      <div className="grid grid-cols-2 gap-2">
                        {clips.map((clip) => (
                          <div
                            key={clip.id}
                            className="relative aspect-video rounded-md overflow-hidden bg-black border border-gray-800 hover:border-gray-600 cursor-pointer group transition-all"
                          >
                            <img
                              src={clip.thumbnail_url || '/placeholder-video.png'}
                              alt=""
                              className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                              <Play className="w-6 h-6 text-white" />
                            </div>
                            <div className="absolute bottom-1 right-1 bg-black/60 px-1.5 py-0.5 rounded text-[10px] text-white font-mono">
                              {formatTime(clip.start_time)}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-6 w-6 text-gray-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all bg-black/40"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle delete
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clips.map((clip) => (
                          <div
                            key={clip.id}
                            className="p-3 bg-[#0a0a0b] rounded-lg border border-gray-800/50 hover:bg-gray-800/50 hover:border-gray-700 transition-all cursor-pointer group"
                          >
                            <div className="flex items-center gap-3">
                              {/* Play Icon Placeholder */}
                              <div className="w-10 h-10 rounded bg-[#1a1a1b] border border-gray-800 flex items-center justify-center flex-shrink-0 group-hover:border-blue-500/50 transition-colors">
                                <Play className="w-4 h-4 text-gray-500 group-hover:text-blue-400 group-hover:fill-current" />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap gap-1.5 mb-1.5">
                                  {clip.play_data?.down && (
                                    <span className="text-[10px] font-bold bg-blue-600/20 text-blue-400 px-2 py-0.5 rounded uppercase tracking-wider">
                                      {clip.play_data.down} & {clip.play_data.distance}
                                    </span>
                                  )}
                                  {clip.play_data?.result && (
                                    <span className="text-[10px] font-bold bg-green-600/20 text-green-400 px-2 py-0.5 rounded uppercase tracking-wider">
                                      {clip.play_data.result}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                                    <span className="flex items-center gap-1 font-mono">
                                      {formatTime(clip.start_time)} → {formatTime(clip.end_time)}
                                    </span>
                                    {clip.play_data?.formation && (
                                      <span className="before:content-['•'] before:mr-1">
                                        {clip.play_data.formation}
                                      </span>
                                    )}
                                  </div>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {clips.length === 0 && (
                      <p className="text-center text-gray-500 py-8">{t('videoDetail.noClips')}</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </TeamGate>
  );
};

export default VideoDetail;
